import {
  AccountCircle as AccountCircleIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  Campaign as CampaignIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Logout as LogoutIcon,
  Mail as MailIcon,
  Notifications as NotificationsIcon,
  ReportProblem as ReportProblemIcon,
  Settings as SettingsIcon,
  Share as ShareIcon,
  Store as StoreIcon,
  Person as PersonIcon
} from "@mui/icons-material";
import HandshakeIcon from '@mui/icons-material/Handshake';
import PostAddIcon from '@mui/icons-material/PostAdd';
import axios from "axios";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import io from 'socket.io-client';
import 'react-toastify/dist/ReactToastify.css';

// ===== Logo Component =====
const Logo = ({ open }) => (
  <div className="flex items-center gap-2 cursor-pointer">
    <img
      src="https://i.postimg.cc/Rhd5hYcN/halfolog-removebg-preview.png"
      alt="Influenceso Logo"
      style={{ width: "45px", height: "auto" }}
    />
    {open && <span className="text-white font-semibold text-xl">Influenceso</span>}
  </div>
);

const Navbar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // Desktop expanded
  const [isMobile, setIsMobile] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [affiliateOpen, setAffiliateOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const socket = io("https://server-side-influencer.onrender.com");
  const influencerId = localStorage.getItem("influencerID");

  useEffect(() => {
    // Auth redirect
    const token = localStorage.getItem("influcertoken");
    if (!token) window.open("https://www.influenceso.in/", "_blank");

    // Register socket
    socket.emit("register", influencerId);
    socket.on("affiliate_approved", (data) => {
      new Notification("✅ Affiliate Request Approved in smesh", {
        body: "You can now generate affiliate links!"
      });
    });

    return () => socket.off("affiliate_approved");
  }, [influencerId]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(";").forEach(cookie => {
      const name = cookie.split("=")[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    window.open("https://www.influenceso.in/", "_blank");
  };

  const getCampaigns = async () => {
    try {
      const res = await axios.get("https://server-side-influencer.onrender.com/brand/getAllCampaigns");
      setCampaigns(res.data.data);
    } catch (err) { console.log(err); }
  };

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`https://server-side-influencer.onrender.com/influencer/${influencerId}/notifications`);
      const data = await res.json();
      if (data.success) setNotifications(data.notifications);
    } catch (err) { console.log(err); }
  };

  useEffect(() => {
    getCampaigns();
    fetchNotifications();
  }, []);

  const liveCampaignCount = campaigns.length;
  const notificationCount = notifications.length;

  const menus = [
    { name: "Profile Analytics", link: "/InfluencerHome", icon: <AssessmentIcon /> },
    { name: "Live Campaigns", link: "/InfluencerHistory", icon: <CampaignIcon />, extra: liveCampaignCount },
    { name: "My Campaigns", link: "/myapplications", icon: <PersonIcon /> },
    { name: "Invitations", link: "/influenecermessage", icon: <MailIcon /> },
    { name: "Notifications", link: "/notifyinflueencers", icon: <NotificationsIcon />, extra: notificationCount },
    { name: "Profile", link: "/InfluencerProfile", icon: <AccountCircleIcon /> },
    { name: "Affiliate Partner", icon: <HandshakeIcon />, onClick: () => setAffiliateOpen(prev => !prev) },
    { name: "Settings", icon: <SettingsIcon />, onClick: () => setSettingsOpen(prev => !prev) },
  ];

  const dropdowns = {
    "Affiliate Partner": [
      { name: "Create Product Link & Share", link: "/createlink", icon: <ShareIcon /> },
      { name: "All Post", link: "/userpost", icon: <PostAddIcon /> },
      { name: "Shop", link: "/shop", icon: <StoreIcon /> },
      { name: "Brands", link: "/partnerbrands", icon: <StoreIcon /> },
      { name: "Analytics", link: "/affiliateanalytics", icon: <BarChartIcon /> },
    ],
    Settings: [
      { name: "Link Account", link: "/linkaccounts", icon: <LinkIcon /> },
      { name: "Update Profile", link: "/InfluencerProfileEdit", icon: <EditIcon /> },
      { name: "Add Complaint", link: "/influencercomplaint", icon: <ReportProblemIcon /> },
    ]
  };

  // === MOBILE VIEW ===
  if (isMobile) {
    return (
      <div className="bg-black w-full p-4 fixed top-0 left-0 z-50 text-white">
        <div className="flex justify-between items-center">
          <Logo open={true} />
          <button onClick={() => setOpen(!open)} className="text-white w-12 h-12 text-3xl flex justify-center items-center rounded-full">☰</button>
        </div>
        {open && (
          <ul className="pt-4">
            {menus.map((menu, i) => (
              <li key={i} className="mb-3">
                <button
                  onClick={(e) => menu.onClick ? menu.onClick(e) : navigate(menu.link)}
                  className="flex items-center justify-between w-full p-2 rounded hover:bg-gray-700"
                >
                  <div className="flex items-center gap-2">
                    {menu.icon}
                    <span>{menu.name}</span>
                    {menu.name === "Live Campaigns" && menu.extra > 0 && (
                      <span className="ml-2 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
                    )}
                    {menu.name === "Notifications" && menu.extra > 0 && (
                      <span className="ml-2 w-4 h-4 bg-red-600 text-white text-xs rounded-full flex items-center justify-center">{menu.extra}</span>
                    )}
                  </div>
                  {(menu.name === "Settings" || menu.name === "Affiliate Partner") && <ArrowDropDownIcon />}
                </button>

                {menu.name === "Settings" && settingsOpen && (
                  <ul className="pl-4 mt-2">
                    {dropdowns.Settings.map((item, idx) => (
                      <li key={idx}>
                        <Link to={item.link} className="flex items-center p-2 rounded hover:bg-gray-700">
                          {item.icon}<span className="ml-2">{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                {menu.name === "Affiliate Partner" && affiliateOpen && (
                  <ul className="pl-4 mt-2">
                    {dropdowns["Affiliate Partner"].map((item, idx) => (
                      <li key={idx}>
                        <Link to={item.link} className="flex items-center p-2 rounded hover:bg-gray-700">
                          {item.icon}<span className="ml-2">{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <li>
              <button onClick={logout} className="flex items-center p-2 w-full rounded hover:bg-gray-700 text-red-500">
                <LogoutIcon /> <span className="ml-2">Logout</span>
              </button>
            </li>
          </ul>
        )}
      </div>
    );
  }

  // === DESKTOP VIEW ===
  return (
    <div className={`bg-black text-white h-screen p-5 pt-8 transition-all duration-300 ${open ? "w-72" : "w-20"}`}
      onMouseEnter={() => !isMobile && setOpen(true)}
      onMouseLeave={() => !isMobile && setOpen(false)}
    >
      <Logo open={open} />
      <ul className="pt-6">
        {menus.map((menu, i) => (
          <li key={i} className="mb-4">
            <button onClick={menu.onClick || (() => navigate(menu.link))} className="flex items-center p-2 w-full rounded hover:bg-gray-700">
              {menu.icon}
              <span className={`${!open && "hidden"} ml-3`}>{menu.name}</span>
              {menu.name === "Live Campaigns" && menu.extra > 0 && (
                <span className="ml-2 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
              )}
              {menu.name === "Notifications" && menu.extra > 0 && (
                <span className="ml-2 w-4 h-4 bg-red-600 text-white text-xs rounded-full flex items-center justify-center">{menu.extra}</span>
              )}
            </button>
            {open && ((menu.name === "Settings" && settingsOpen) || (menu.name === "Affiliate Partner" && affiliateOpen)) && (
              <ul className="pl-6 mt-2">
                {dropdowns[menu.name].map((item, idx) => (
                  <li key={idx}>
                    <Link to={item.link} className="flex items-center p-2 rounded hover:bg-gray-700">
                      {item.icon}<span className="ml-2">{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
        <li>
          <button onClick={logout} className="flex items-center p-2 w-full rounded hover:bg-gray-700 text-red-500">
            <LogoutIcon /><span className={`${!open && "hidden"} ml-3`}>Logout</span>
          </button>
        </li>
      </ul>
    </div>
  );
};

export default Navbar;